interface AuthLocationState {
  from?: unknown;
}

function normalizeSameOriginPath(value: unknown): string | null {
  if (
    typeof value !== 'string' ||
    !value.startsWith('/') ||
    value.includes('\\')
  ) {
    return null;
  }

  const hasControlCharacter = Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 31 || codePoint === 127;
  });
  if (hasControlCharacter) return null;

  try {
    const currentOrigin = window.location.origin;
    const parsedUrl = new URL(value, currentOrigin);
    if (parsedUrl.origin !== currentOrigin) return null;

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch {
    return null;
  }
}

/**
 * Only allow normalized same-origin application paths after authentication.
 * This prevents externally supplied values from becoming open redirects.
 */
export function getSafeRedirectPath(
  value: unknown,
  fallback = '/',
): string {
  return (
    normalizeSameOriginPath(value) ??
    normalizeSameOriginPath(fallback) ??
    '/'
  );
}

/** Resolve the redirect shared by guarded routes and explicit query strings. */
export function getAuthRedirect(
  search: string,
  state: unknown,
  fallback = '/',
): string {
  const queryRedirect = new URLSearchParams(search).get('redirect');
  const stateRedirect =
    state && typeof state === 'object'
      ? (state as AuthLocationState).from
      : undefined;

  return (
    normalizeSameOriginPath(queryRedirect) ??
    normalizeSameOriginPath(stateRedirect) ??
    getSafeRedirectPath(fallback)
  );
}
